export type ObservableCallback = () => Promise<void> | void;

// How often an open tab asks the server whether a new version was deployed
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

export class VersionUpdateServices {
  private subscribers: ObservableCallback[] = [];
  private updatedWorker?: ServiceWorker;
  private isRefreshing = false;
  // This tab asked the new version to take over, so it reloads without asking
  private isActivating = false;

  constructor() {
    this.initControllerChangeListener();
  }

  private initControllerChangeListener() {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
      return;
    }

    // NOTE: With no service worker controlling the page at load, the page came
    // from the network and is already the latest version
    const hadControllerAtLoad = !!navigator.serviceWorker.controller;

    // Listen for when the new service worker takes control of the page.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // NOTE: Lock to prevent multiple simultaneous reloads
      // This is crucial because If there multiple open tabs
      // the 'controllerchange' event might run multiple times
      if (this.isRefreshing || !hadControllerAtLoad) {
        return;
      }
      // NOTE: Another tab switched versions; this one may hold a half-filled
      // form, so it asks before reloading. Its old code keeps working if not.
      if (
        !this.isActivating &&
        !window.confirm(
          'DROPS was updated in another tab. Reload this tab now?'
        )
      ) {
        return;
      }
      this.isRefreshing = true;
      this.reloadPage();
    });
  }

  private reloadPage() {
    window.location.reload();
  }

  activateLatestVersion() {
    if (this.updatedWorker) {
      // NOTE: By default, service workers wait until all tabs with old version are closed
      // By sending SKIP_WAITING, we force immediate activation of the new worker.
      // This provides instant updates but requires a page reload
      this.isActivating = true;
      this.updatedWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  async checkForUpdates() {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
      return;
    }

    // Get the current service worker registrations
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }

    // NOTE: A version that finished downloading earlier (its update was
    // declined, or every tab stayed open) is waiting. The app has only just
    // opened, so nothing is lost by switching to it now without asking.
    if (registration.waiting) {
      this.updatedWorker = registration.waiting;
      this.activateLatestVersion();
      return;
    }

    // NOTE: The 'updatefound' event fires when
    // Registration.update() finds a new service worker file on the server
    // a new service worker begins downloading and installing

    // Listen for new service workers being discovered and installed.
    registration.addEventListener('updatefound', () => {
      // Get the new service worker that's being installed
      const newWorker = registration.installing;
      if (!newWorker) {
        return;
      }

      // Monitor the new service worker's lifecycle states.
      const onWorkerStateChange = () => {
        // Check if the new worker has finished installing AND there's already
        // an active service worker controlling the page
        const isReadyToUpdate =
          newWorker.state === 'installed' && navigator.serviceWorker.controller;
        if (isReadyToUpdate) {
          this.notifySubscribers(newWorker);
        }
      };
      newWorker.addEventListener('statechange', onWorkerStateChange);
    });

    // NOTE: The browser automatically checks for updates:
    // - When navigating to a page in scope
    // - Every 24 hours (approximately)
    // - When registration.update() is called
    // DROPS changes pages without navigating, so a tab left open never
    // learns of a deploy unless it asks; ask hourly and when the tab returns.
    const checkServer = () => registration.update().catch(() => undefined);

    // Manually check the server for a new service worker version.
    checkServer();
    window.setInterval(checkServer, UPDATE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkServer();
      }
    });
  }

  setRegistration(registration: ServiceWorkerRegistration) {
    if (registration.waiting) {
      // Manually set worker registration
      this.notifySubscribers(registration.waiting);
    }
  }

  subscribeForUpdates(callback: ObservableCallback) {
    this.subscribers.push(callback);
  }

  unsubscribeFromUpdates(callback: ObservableCallback) {
    const index = this.subscribers.indexOf(callback);
    if (index >= 0) {
      this.subscribers = this.subscribers.toSpliced(index, 1);
    }
  }

  isUpdateAvailable() {
    return !!this.updatedWorker;
  }

  private notifySubscribers(newWorker: ServiceWorker) {
    // NOTE: Registration and checkForUpdates both report the same new worker;
    // ask about it once
    if (this.updatedWorker === newWorker) {
      return;
    }
    this.updatedWorker = newWorker;
    this.subscribers.forEach((callback) => callback());
  }
}

export const versionUpdateServices = new VersionUpdateServices();
